import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {EnvironmentService} from '../services/environment.service';
import {ModelCacheUtil} from '../utils/model-cache-util';
import {SourcingMatrixReport} from '@app/models/sourcing-matrix-report.model';
import {UtilService} from '@app/services/util.service';

@Injectable({
  providedIn: 'root'
})
export class SourcingMatrixService {

  /**
   * Cached list of optimization reports
   */
  private cache: ModelCacheUtil<SourcingMatrixReport>;

  constructor(private http: HttpClient, private environmentService: EnvironmentService, private utilService: UtilService) {
    this.cache = new ModelCacheUtil<SourcingMatrixReport>();
  }

  get sourcingMatrixReports(): Array<SourcingMatrixReport> {
    return this.cache.cachedModels;
  }

  getNewReportName(reportNameMaxLength: number): string {
    const cachedReports = this.sourcingMatrixReports;
    const prependName = 'Untitled Sourcing Matrix';
    return this.utilService.generateReportName(prependName, reportNameMaxLength, cachedReports);
  }

  getDuplicateReportName(prependName: string, reportNameMaxLength: number): string {
    const cachedReports = this.cache.cachedModels;
    return this.utilService.generateDuplicateReportName(prependName, reportNameMaxLength, cachedReports);
  }

  fetchAll(projectId: number, modelRunId: string): Observable<Array<SourcingMatrixReport>> {
    const env = this.environmentService.environment.authProxy;
    const url = `${env.url}/${env.lpoSimulatorContextPath}/projects/${projectId}/runs/${modelRunId}/sourcingMatrixReports`;
    return this.http.get<Array<SourcingMatrixReport>>(url).pipe(map((reports: SourcingMatrixReport[]) => {
      this.cache.clear();
      this.cache.appendAll(reports);
      return reports;
    }));
  }

  refreshCachedModel(updatedData: SourcingMatrixReport): SourcingMatrixReport {
    if (updatedData) {
      return this.cache.refresh(updatedData);
    }
    return null;
  }

  /**
   * Lists all price elasticity reports associated with given project and run id.
   */
  getAll(projectId: number, modelRunId: string): Observable<Array<SourcingMatrixReport>> {
    const reports = this.cache.filter((it: SourcingMatrixReport) => {
      return it.projectId === projectId && it.modelRunId === modelRunId;
    });
    if (reports.length) {
      return of(reports);
    } else {
      return this.fetchAll(projectId, modelRunId);
    }
  }

  add(report: SourcingMatrixReport): Observable<SourcingMatrixReport> {
    const env = this.environmentService.environment.authProxy;
    const url = `${env.url}/${env.lpoSimulatorContextPath}/projects/${report.projectId}/runs/${report.modelRunId}/sourcingMatrixReports`;
    return this.http.post<SourcingMatrixReport>(url, {sourcingMatrixReport: report}).pipe(map((newReport: SourcingMatrixReport) => {
      this.cache.append(newReport);
      return newReport;
    }));
  }

  delete(report: SourcingMatrixReport): Observable<string> {
    const env = this.environmentService.environment.authProxy;
    const projectId = report.projectId;
    const modelRunId = report.modelRunId;
    const id = report.id;
    const contextPath = `${env.url}/${env.lpoSimulatorContextPath}`;
    const url = `${contextPath}/projects/${projectId}/runs/${modelRunId}/sourcingMatrixReports/${id}`;
    return this.http.delete<SourcingMatrixReport>(url).pipe(map(() => {
      this.cache.remove(report);
      return report.id;
    }));
  }

  /**
   * Method is use to download file.
   * @param data - Array Buffer data
   * @param type - type of the document.
   */
  generateReport(report: SourcingMatrixReport): Observable<ArrayBuffer> {
    const env = this.environmentService.environment.authProxy;
    const url = `${env.url}/${env.lpoSimulatorContextPath}/projects/${report.projectId}/runs/${report.modelRunId}/sourcingMatrixReports/${report.id}/download`;
    return this.http.get(`${url}`, {
        responseType: 'arraybuffer'
      }
    );
  }

  /**
   * Returns single sourcing matrix report associated with given project and run id.
   */
  getById(id: string, params: { projectId: number; modelRunId: string }): Observable<SourcingMatrixReport> {
    const report = this.cache.cachedModels.find((it: SourcingMatrixReport) => {
      return it.id === id;
    });
    if (report) {
      return of(report);
    } else {
      const env = this.environmentService.environment.authProxy;
      const contextPath = `${env.url}/${env.lpoSimulatorContextPath}`;
      const url = `${contextPath}/projects/${params.projectId}/runs/${params.modelRunId}/sourcingMatrixReports/${id}`;
      return this.http.get<SourcingMatrixReport>(url).pipe(map((singleReport: SourcingMatrixReport) => {
        this.cache.append(singleReport);
        return singleReport;
      }));
    }
  }
  
   /**
   * Method is used to generate Power BI report
   */
    powerBIReportGeneration(report: SourcingMatrixReport): Observable<any> {
      const env = this.environmentService.environment.authProxy;
      const url = `${env.url}/${env.lpoSimulatorContextPath}/projects/${report.projectId}/runs/${report.modelRunId}/sourcingMatrixReports/${report.id}/powerBIReportGeneration`;
      return this.http.get(url);
    }
}
